import React, { useMemo } from 'react';
import { CANALES_DISPONIBLES, tiposConversionList } from '../../../data/listasDesplegables';
import { PercentInput } from '../../../utils/FormInputs';

/**
 * SectionComercial - Componente modular para el equipo comercial, comisiones y contadores de Patrimoniales.
 * Optimizado con cálculos en memoria (useMemo), inputs atómicos de porcentaje y clases nativas de Tailwind.
 * Corrección: Desbloqueo absoluto de comisiones para permitir edición manual en cualquier flujo de carga o edición.
 */
const SectionComercial = ({ state, actions }) => {
    // Extraemos lo necesario de los objetos agrupados
    const { formData, dbMasters, dbSubcanales } = state;
    const { 
        handleChange, 
        handleCanalTypeChange, 
        handleCanalIdChange, 
        handleSubcanalIdChange, 
        handleContadorToggle,
        setFormData 
    } = actions;

    // --- MEJORA MANUAL COMISIÓN DE CANAL: Manejador para evitar el loop del useEffect en usePolicyLogic ---
    const handleCanalComisionInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value,
            manualCanalComisionOverride: true // Rompe el ciclo del useEffect automático
        }));
    };

    // --- ORDENAMIENTO EN MEMORIA ULTRA EFICIENTE (useMemo) ---
    const sortedCanalesDisponibles = useMemo(() => {
        return [...(CANALES_DISPONIBLES || [])].sort((a, b) => a.localeCompare(b));
    }, []);

    const filteredAndSortedCanales = useMemo(() => {
        const list = dbMasters?.canales || [];
        return list
            .filter(c => c.canal === formData.canal)
            .sort((a, b) => {
                const nameA = a.nombreDisplay || a.nombre || '';
                const nameB = b.nombreDisplay || b.nombre || '';
                return nameA.localeCompare(nameB);
            });
    }, [dbMasters?.canales, formData.canal]);

    const sortedSubcanales = useMemo(() => {
        return [...(dbSubcanales || [])].sort((a, b) => 
            (a.nombre || '').localeCompare(b.nombre || '')
        );
    }, [dbSubcanales]);

    const sortedContadores = useMemo(() => {
        const list = (dbMasters?.contadores || []).map(c => ({
            ...c,
            nombreCompleto: `${c.nombre || ''} ${c.apellido || ''}`.trim()
        }));
        return list.sort((a, b) => a.nombreCompleto.localeCompare(b.nombreCompleto));
    }, [dbMasters?.contadores]);

    const sortedColaboradores = useMemo(() => {
        return [...(dbMasters?.colaboradores || [])].sort((a, b) => 
            (a.nombre || '').localeCompare(b.nombre || '')
        );
    }, [dbMasters?.colaboradores]);

    const sortedTiposConversion = useMemo(() => {
        return [...(tiposConversionList || [])].sort((a, b) => a.localeCompare(b));
    }, []);

    return (
        <fieldset className="border border-blue-200 rounded-xl p-6 bg-blue-50/20 text-left shadow-sm animate-fade-in transition-all duration-300">
            <legend className="px-3 text-lg font-bold text-blue-800 tracking-tight">Equipo Comercial & Comisiones</legend>
            
            {/* FILA 1: CANALES Y PARTICIPACIONES */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2 items-end">
                {/* 1. TIPO DE CANAL */}
                <div>
                    <label className="label-form text-slate-500 font-bold mb-1 block">Canal *</label>
                    <select 
                        name="canal"
                        value={formData.canal || ''} 
                        onChange={(e) => handleCanalTypeChange(e.target.value)} 
                        className="input-round font-bold border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all duration-200" 
                        required
                    >
                        <option value="">Seleccione Canal...</option>
                        {sortedCanalesDisponibles.map(c => (
                            <option key={c} value={c}>
                                {c}
                            </option>
                        ))}
                    </select>
                </div>

                {/* 2. NOMBRE DEL CANAL + % PARTICIPACIÓN */}
                <div>
                    <label className="label-form text-slate-500 font-bold mb-1 block">Nombre del Canal*</label>
                    <div className="flex gap-2 items-end h-[46px]">
                        <select 
                            name="canalNombreId" 
                            value={formData.canalNombreId || ''} 
                            onChange={(e) => handleCanalIdChange(e.target.value)} 
                            className="input-round border-slate-200 flex-grow focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all duration-200" 
                            disabled={!formData.canal} 
                            required
                        >
                            <option value="">Seleccione...</option>
                            {filteredAndSortedCanales.map(c => (
                                <option key={c.id} value={c.id}>
                                    {c.nombreDisplay || c.nombre}
                                </option>
                            ))}
                        </select>
                        <div className="w-24 shrink-0">
                            {/* ◄ MEJORA SENIOR: Se removió disabled={!habilitarEdicion} para asegurar la modificación manual siempre */}
                            <PercentInput 
                                name="comisionOrganizador"
                                value={formData.comisionOrganizador} 
                                onChange={handleCanalComisionInputChange} 
                                disabled={false} 
                                textColor="text-orange-700 text-center font-bold"
                                focusColor="focus-within:border-orange-300 focus-within:ring-orange-100"
                                placeholder="0"
                            />
                        </div>
                    </div>
                </div>

                {/* 3. SUBCANAL ASOCIADO + % PARTICIPACIÓN */}
                <div>
                    <label className="label-form text-slate-500 font-bold mb-1 block">Subcanal Asociado</label>
                    <div className="flex gap-2 items-end h-[46px]">
                        <select 
                            name="subcanalNombreId"
                            value={formData.subcanalNombreId || ''} 
                            onChange={(e) => handleSubcanalIdChange(e.target.value)} 
                            className="input-round bg-white border-slate-200 flex-grow focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all duration-200" 
                            disabled={sortedSubcanales.length === 0}
                        >
                            <option value="">(Ninguno)</option>
                            {sortedSubcanales.map(s => (
                                <option key={s.id} value={s.id}>
                                    {s.nombre}
                                </option>
                            ))}
                        </select>
                        {formData.subcanalNombreId && (
                            <div className="w-24 shrink-0 animate-fade-in">
                                <PercentInput 
                                    name="comisionAsociado"
                                    value={formData.comisionAsociado} 
                                    onChange={handleCanalComisionInputChange} 
                                    textColor="text-emerald-700 text-center font-bold"
                                    focusColor="focus-within:border-emerald-300 focus-within:ring-emerald-100"
                                    placeholder="0"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* FILA 2: CONTADOR, VENDEDOR Y SUSCRIPTOR */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
                {/* 4. CONTADOR */}
                <div className="flex flex-col">
                    <label className="label-form text-slate-500 font-bold mb-1 block">¿Interviene Contador?</label>
                    <div className="flex gap-2 h-[46px]">
                        <label className={`flex items-center justify-center cursor-pointer px-3 rounded-xl border transition-all duration-300 shrink-0 select-none ${
                            formData.tieneContador ? 'bg-blue-600 border-blue-700 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-50'
                        }`}>
                            <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-colors duration-300 ${
                                formData.tieneContador ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-300'
                            }`}>
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <input 
                                type="checkbox" 
                                checked={formData.tieneContador || false} 
                                onChange={(e) => handleContadorToggle(e.target.checked)} 
                                className="sr-only"
                            />
                        </label>

                        <div className="flex-grow">
                            {formData.tieneContador ? (
                                <select 
                                    name="contadorId"
                                    value={formData.contadorId || ''} 
                                    onChange={(e) => {
                                        const c = sortedContadores.find(x => x.id === e.target.value);
                                        setFormData(p => ({
                                            ...p, 
                                            contadorId: e.target.value, 
                                            contadorNombre: c ? c.nombreCompleto : ''
                                        }));
                                    }} 
                                    className="input-round border-blue-300 bg-white font-bold animate-fade-in text-sm text-slate-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 transition-all duration-200"
                                >
                                    <option value="">Seleccionar Contador...</option>
                                    {sortedContadores.map(c => (
                                        <option key={c.id} value={c.id}>
                                            {c.nombreCompleto}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <div className="input-round border-slate-100 bg-slate-50/50 flex items-center px-4 text-slate-400 text-xs italic select-none h-full rounded-xl">
                                    No aplica
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* 5. VENDEDOR */}
                <div>
                    <label className="label-form text-slate-500 font-bold mb-1 block">Vendedor *</label>
                    <select 
                        name="vendedor"
                        value={formData.vendedor || ''} 
                        onChange={handleChange} 
                        className="input-round border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all duration-200"
                        required
                    >
                        <option value="">Seleccione...</option>
                        {sortedColaboradores.map(c => (
                            <option key={c.id} value={c.nombre}>
                                {c.nombre}
                            </option>
                        ))}
                    </select>
                </div>
                
                {/* 6. SUSCRIPTOR */}
                <div>
                    <label className="label-form text-slate-500 font-bold mb-1 block">Suscriptor *</label>
                    <select 
                        name="suscriptor"
                        value={formData.suscriptor || ''} 
                        onChange={handleChange} 
                        className="input-round border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all duration-200"
                        required
                    >
                        <option value="">Seleccione...</option>
                        {sortedColaboradores.map(c => (
                            <option key={c.id} value={c.nombre}>
                                {c.nombre}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* FILA 3: TIPO DE CONVERSIÓN */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6">
                <div className="flex flex-col">
                    <label className="label-form text-slate-500 font-bold mb-1 block">Tipo de Conversión</label>
                    <select 
                        name="tipoConversion"
                        value={formData.tipoConversion || ''} 
                        onChange={handleChange} 
                        className="input-round border-slate-200 font-bold focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all duration-200"
                    >
                        <option value="">Seleccione...</option>
                        {sortedTiposConversion.map(t => (
                            <option key={t} value={t}>
                                {t}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
        </fieldset>
    );
};

export default SectionComercial;